import React, { Component } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import {ErrorBoundary} from 'react-error-boundary'
import Slider from 'react-slick';
import Link from '../Link';
import SvgIcon from '../SvgIcon';

import './CardsCarousel.scss';

function ErrorFallback({error, resetErrorBoundary}) {
  return (
    <div role="alert">
      <p>Something went wrong:</p>
      <pre>{error.message}</pre>
      <button onClick={resetErrorBoundary}>Try again</button>
    </div>
  )
}  

class CardsCarousel extends Component {
  static defaultProps = {
    cardsInFrame: {
      small: 2,
      medium: 3,
      large: 5,
      xxlarge: 6
    },
    dots: false,
    arrows: true
  }

  constructor(props) {
    super(props);

    // Reference: https://github.com/FormidableLabs/nuka-carousel/issues/16#issuecomment-165244283
    this.state = {
      carousels: {},
      currIdx: 0
    };
  }

  componentDidMount() {
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 50);
    if(!this.props.gcr && this.props.isRtl && this.slider) {
      let x = this.props.children.length - this.noOfCards()
      this.slider.slickGoTo(x, true);
      this.setState({currIdx: x})
    }

    if(this.props.gcr && this.slider) {
      let y = this.props.isRtl ? (this.props.children.length-this.props.index)-1 : this.props.index

      this.slider.slickGoTo(y, true);
      this.setState({currIdx: y})
    }
  }

  resetSlides = () => {
    if(this.props.isRtl && this.slider) {
      let x = this.props.children.length - this.noOfCards()
      this.slider.slickGoTo(x, true);
      this.setState({currIdx: x})
    }
    else {
      this.slider.slickGoTo(0, true);
      this.setState({currIdx: 0})
    }
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(this.props.children, prevProps.children)) {
      //Added time-out as per https://github.com/FormidableLabs/nuka-carousel/issues/103#issuecomment-233661161

      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 0);
      if(this.props.children && this.props.children.length>0 && prevProps.children && prevProps.children.length>0 ) {
        var result = this.props.children.filter(function(o1){
          return !prevProps.children.some(function(o2){
              return o1.key === o2.key;
          });
        })
        if(result.length > 0) {
          this.resetSlides()
        }
      }
    }

    if(this.props.gcr && this.slider && !isEqual(this.props.index, prevProps.index)) {
      let y = this.props.isRtl ? (this.props.children.length-this.props.index)-1 : this.props.index

      this.slider.slickGoTo(y, true);
      this.setState({currIdx: y})
    }
  }

  setCarouselData(carousel) {
    var data = this.state.carousels;
    data[carousel] = this.refs[carousel];
    this.setState({
      carousels: data
    });
  }

  noOfCards = () => {
    const { viewport, cardsInFrame } = this.props;
    let cards = 1;
    if (viewport.xxlarge && cardsInFrame.xxlarge) {
      cards = cardsInFrame.xxlarge;
    } else if (viewport.xlarge && cardsInFrame.xlarge) {
      cards = cardsInFrame.xlarge;
    } else if (viewport.large && cardsInFrame.large) {
      cards = cardsInFrame.large;
    } else if (viewport.medium && cardsInFrame.medium) {
      cards = cardsInFrame.medium;
    } else if (viewport.small && cardsInFrame.small) {
      cards = cardsInFrame.small;
    }
    return cards;
  }

  render() {
    const baseClassName = 'pb-cards-carousel';
    const {
      children,
      parentClassName,
      currentIndex,
      highlightCurrent,
      popoutControls,
      dots,
      isRtl,
      arrows,
      adaptiveHeight,
      autoplay,
      customClick,
      nextIcon,
      prevIcon
    } = this.props;

    let cards = this.noOfCards()

    const classes = {
      [baseClassName]: true,
      [`${baseClassName}--highlight-current`]: highlightCurrent,
      [`${baseClassName}--popout-controls`]: popoutControls,
      [parentClassName]: parentClassName
    };

    let settings = {
      infinite: false,
      slidesToShow: cards,
      slidesToScroll: cards,
      slideIndex: this.state.currIdx,
      dots: dots,
      arrows: arrows,
      lazyLoad: !isRtl ? "ondemand" : null,
      rtl: isRtl,
      afterChange: (next) => {this.setState({ currIdx: next }); if(customClick) customClick(next)},
      adaptiveHeight: adaptiveHeight,
      autoplay:autoplay,
      speed: 500,
      nextArrow: <Link
                  parentClassName={`${baseClassName}__link ${baseClassName}__link--next`}
                  disabled={this.state.currIdx >= children.length - cards}
                  theme="dark"
                >
                  <SvgIcon name={nextIcon ? nextIcon : "chevron-right"}/>
                </Link>,
      prevArrow: <Link
                  parentClassName={`${baseClassName}__link ${baseClassName}__link--previous`}
                  disabled={this.state.currIdx === 0}
                  theme="dark"
                >
                  <SvgIcon name={prevIcon ? prevIcon :"chevron-left"}/>
                </Link>,
    };

    return (
      <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
      >
      <div className={classNames(classes)}>
        <Slider ref={slider => (this.slider = slider)} {...settings}>
          {
            React.Children
              .toArray(children)
              .map((child, i) => {
                const cardBaseClassName = `${baseClassName}__card`;
                const cardClasses = {
                  [cardBaseClassName]: true,
                  [`${cardBaseClassName}--active`]: currentIndex >= 0 && i === currentIndex
                };
                return (
                  <div className={classNames(cardClasses)} key={`${baseClassName}__card-${i}`}>
                    {child}
                  </div>
                )
              })
          }
        </Slider>
      </div>
      </ErrorBoundary>
    );
  }
}

CardsCarousel.propTypes = {
  children: PropTypes.node.isRequired,
  viewport: PropTypes.object.isRequired,
  parentClassName: PropTypes.string,
  cardsInFrame: PropTypes.object,
  currentIndex: PropTypes.number,
  highlightCurrent: PropTypes.bool,
  popoutControls: PropTypes.bool
};

export default CardsCarousel;



// WEBPACK FOOTER //
// ./src/components/CardsCarousel/CardsCarousel.jsx